(() => {
	"use strict";

	/**
	 * ARD-002 | Cart slide-out drawer - Shopify production version
	 *
	 * Replaces the full-page cart navigation with a slide-out drawer. Opening the
	 * cart icon or adding a product to the bag reveals the drawer, which renders the
	 * current cart lines, per-line quantity controls, a "Keep Exploring" cross-sell
	 * block, an estimated total and checkout / view-bag CTAs. All cart reads and
	 * writes use Shopify's AJAX cart endpoints; the drawer never navigates away.
	 */

	const testId = "ARD-002";

	/* ---------------------------------------------------------------------------
	 * Configuration
	 * ------------------------------------------------------------------------- */

	const SHOP_ROOT = window.Shopify?.routes?.root || "/";

	const ENDPOINTS = {
		cart: `${SHOP_ROOT}cart.js`,
		add: `${SHOP_ROOT}cart/add.js`,
		change: `${SHOP_ROOT}cart/change.js`,
		recs: (productId) =>
			`${SHOP_ROOT}recommendations/products.json?product_id=${productId}&limit=10&intent=related`,
	};

	const SELECTORS = {
		cartLink: 'a[href="/cart"], a[href$="/cart"]',
		cartBubble: ".cart-count-bubble",
	};

	// Cross-sell price band (Shopify money is in cents) and how many recs to show.
	const MIN_REC_PRICE = 59000;
	const MAX_REC_PRICE = 1000000;
	const REC_LIMIT = 2;

	const LIMIT_MSG_CLASS = `${testId}_limit-msg`;

	/* ---------------------------------------------------------------------------
	 * Icon + drawer markup
	 * ------------------------------------------------------------------------- */

	const CLOSE_ICON_SVG = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.4565 0.331055L0.331543 13.4561M13.4565 13.4561L0.331543 0.331055" stroke="#1A1A1A" stroke-width="0.9375"/></svg>`;

	const REMOVE_ICON_SVG = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.5 9V13.25M8.5 9V13.25M5.75 12.2V6H14.25C14.25 8.421 14.25 9.779 14.25 12.2C14.25 13.053 14.25 13.647 14.212 14.11C14.175 14.563 14.106 14.824 14.005 15.021C13.7822 15.4476 13.4408 15.8008 13.022 16.038C12.825 16.138 12.564 16.208 12.111 16.245C11.648 16.282 11.054 16.283 10.201 16.283H9.801C8.948 16.283 8.354 16.283 7.891 16.245C7.438 16.208 7.177 16.139 6.98 16.038C6.56083 15.8009 6.21911 15.4478 5.996 15.021C5.896 14.824 5.826 14.563 5.789 14.11C5.75 13.647 5.75 13.053 5.75 12.2Z" stroke="#121212" stroke-opacity="0.81" stroke-width="1.5" stroke-linecap="round"/><path d="M4.25 6H15.75M8 5.25C8 4.71957 8.21071 4.21086 8.58579 3.83579C8.96086 3.46071 9.46957 3.25 10 3.25C10.5304 3.25 11.0391 3.46071 11.4142 3.83579C11.7893 4.21086 12 4.71957 12 5.25" stroke="#121212" stroke-opacity="0.81" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

	const MINUS_ICON_SVG = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.66992 6.79492H10.921H2.66992Z" fill="#121212"/><path d="M2.66992 6.79492H10.921" stroke="#121212" stroke-width="1.45607" stroke-linecap="round"/></svg>`;

	const PLUS_ICON_SVG = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.66992 6.79492H10.921H2.66992Z" fill="#121212"/><path d="M2.66992 6.79492H10.921" stroke="#121212" stroke-width="1.45607" stroke-linecap="round"/><path d="M6.79492 2.66992V10.921" stroke="#121212" stroke-width="1.45607" stroke-linecap="round"/></svg>`;

	const DRAWER_HTML = `
		<div class="${testId}_overlay" aria-hidden="true"></div>
		<aside class="${testId}_drawer" role="dialog" aria-label="Shopping bag" aria-hidden="true">
			<div class="${testId}_drawer-header">
				<span class="${testId}_drawer-title">Your cart</span>
				<button class="${testId}_drawer-close" type="button" aria-label="Close shopping bag">${CLOSE_ICON_SVG}</button>
			</div>

			<div class="${testId}_drawer-body"></div>

			<div class="${testId}_drawer-footer" hidden>
				<div class="${testId}_total">
					<span class="${testId}_total-label">Estimated total</span>
					<strong class="${testId}_total-value">R 0.00</strong>
				</div>

				<p class="${testId}_footer-note ${testId}_footer-note-shipping">
					Complimentary worldwide shipping on all orders
				</p>

				<p class="${testId}_footer-note ${testId}_footer-note-tax">
					<span class="${testId}_footer-note-underline">International taxes and duties</span> not included.
					Only RSA taxes included.
					<span class="${testId}_footer-note-underline">Please note</span>:
					all orders are charged in ZAR at checkout.
				</p>

				<div class="${testId}_footer-ctas">
					<a class="${testId}_checkout" href="${SHOP_ROOT}checkout">Checkout</a>
					<a class="${testId}_viewcart" href="${SHOP_ROOT}cart">View bag</a>
				</div>
			</div>
		</aside>
	`;

	/* ---------------------------------------------------------------------------
	 * State
	 * ------------------------------------------------------------------------- */

	let rootEl;
	let overlayEl;
	let drawerEl;
	let drawerTitleEl;
	let drawerBodyEl;
	let drawerFooterEl;
	let totalValueEl;
	let checkoutEl;
	let viewCartEl;

	let renderInFlight = null;
	let lastAddedProductId = null;
	let cartBusy = false;
	let lastCart = null;

	const recsCache = new Map();
	const productCache = new Map();

	/* ---------------------------------------------------------------------------
	 * Money formatting
	 * ------------------------------------------------------------------------- */

	/** Format a cents amount as a plain ZAR string. */
	const money = (cents) =>
		"R " +
		(cents / 100).toLocaleString("en-ZA", {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});

	/**
	 * Format a cents amount using the site's BA currency plugin so drawer prices
	 * match the visitor's selected currency; fall back to ZAR.
	 *
	 * widgetConv expects major units (Rands), so convert from Shopify's cents.
	 */
	const formatMoney = (cents) =>
		window.baCurr?.widgetConv
			? window.baCurr.widgetConv(cents / 100)
			: money(cents);

	/** Escape a string for safe interpolation into innerHTML. */
	const escapeHtml = (str) =>
		String(str).replace(
			/[&<>"']/g,
			(c) =>
				({
					"&": "&amp;",
					"<": "&lt;",
					">": "&gt;",
					'"': "&quot;",
					"'": "&#39;",
				})[c]
		);

	/* ---------------------------------------------------------------------------
	 * Cart + product data
	 * ------------------------------------------------------------------------- */

	/** Fetch the authoritative cart from Shopify and cache it. */
	const getCart = async () => {
		const res = await fetch(ENDPOINTS.cart, {
			headers: {
				Accept: "application/json",
			},
		});

		lastCart = await res.json();

		return lastCart;
	};

	/** Whether the cart contains an item that requires order review. */
	const cartRequiresReview = (cart) =>
	!!cart?.items?.some(
		(item) =>
			String(
				item.properties?._requires_order_review
			) === "true"
	);

	/**
	 * Fetch related-product recommendations within the price band,
	 * capped at REC_LIMIT.
	 */
	const getRecommendations = async (productId) => {
		if (recsCache.has(productId)) {
			return recsCache.get(productId);
		}

		const res = await fetch(ENDPOINTS.recs(productId), {
			headers: {
				Accept: "application/json",
			},
		});

		const data = await res.json();

		const products = (data.products || [])
			.filter(
				(p) =>
					p.price >= MIN_REC_PRICE &&
					p.price <= MAX_REC_PRICE
			)
			.slice(0, REC_LIMIT);

		recsCache.set(productId, products);

		return products;
	};

	/** Sync the theme's cart-count bubble(s) with the current item count. */
	const updateBubble = async (cartArg) => {
		const cart = cartArg || (await getCart());

		if (!cart) {
			return;
		}

		lastCart = cart;

		const count = cart.item_count;

		/*
		 * Preserve the theme's native bubble structure — the inner aria-hidden
		 * span carries the parenthesised count and the visible font-size;
		 * a bare text node renders at 0.
		 */
		document
			.querySelectorAll(SELECTORS.cartBubble)
			.forEach((bubbleEl) => {
				bubbleEl.innerHTML =
					count > 0
						? `<span aria-hidden="true">(${count})</span>`
						: "";
			});
	};

	/** Fetch a product's JSON by handle and cache it. */
	const getProductByHandle = async (handle) => {
		if (!handle) {
			return null;
		}

		if (productCache.has(handle)) {
			return productCache.get(handle);
		}

		const res = await fetch(
			`${SHOP_ROOT}products/${handle}.js`,
			{
				headers: {
					Accept: "application/json",
				},
			}
		);

		if (!res.ok) {
			return null;
		}

		const product = await res.json();

		productCache.set(handle, product);

		return product;
	};

	/**
	 * Effective per-cart max =
	 * min(Shopify quantity_rule.max, deny-policy stock cap).
	 */
	const effectiveMaxFor = (variant) => {
		if (!variant) {
			return Infinity;
		}

		const ruleCap =
			variant.quantity_rule?.max ?? Infinity;

		const stockCap =
			variant.inventory_management === "shopify" &&
			variant.inventory_policy === "deny"
				? variant.inventory_quantity
				: Infinity;

		return Math.min(ruleCap, stockCap);
	};

	/**
	 * Resolve the max allowed quantity and how many are already
	 * in the cart for a variant.
	 */
	const getLimitContext = async (variantId, handle) => {
		const [product, cart] = await Promise.all([
			getProductByHandle(handle),
			getCart(),
		]);

		const variant =
			product && product.variants
				? product.variants.find(
						(v) =>
							String(v.id) ===
							String(variantId)
					)
				: null;

		const inCart =
			cart && cart.items
				? cart.items
						.filter(
							(i) =>
								String(i.id) ===
								String(variantId)
						)
						.reduce(
							(sum, i) =>
								sum + i.quantity,
							0
						)
				: 0;

		return {
			variant,
			max: effectiveMaxFor(variant),
			inCart,
		};
	};

	/**
	 * Derive the current product handle from the URL
	 * or Shopify analytics meta.
	 */
	const getHandleFromPage = () => {
		const fromPath = (
			window.location.pathname.match(
				/\/products\/([^\/?#]+)/
			) || []
		)[1];

		return (
			fromPath ||
			window.ShopifyAnalytics?.meta?.product?.handle ||
			null
		);
	};

	/* ---------------------------------------------------------------------------
	 * Limit messaging
	 * ------------------------------------------------------------------------- */

	/** Show a limit message against a specific line in the drawer, if present. */
	const showLineMessage = (variantId, msg) => {
		if (!drawerBodyEl || !variantId) {
			return false;
		}

		const lineEl = drawerBodyEl.querySelector(
			`.${testId}_line[data-variant-id="${variantId}"]`
		);

		if (!lineEl) {
			return false;
		}

		showLimitMessage(lineEl, msg);

		return true;
	};

	/**
	 * Render (or replace) a transient limit message inside a host element;
	 * auto-hides after 6s.
	 */
	const showLimitMessage = (hostEl, msg) => {
		if (!hostEl || !msg) {
			return;
		}

		let noteEl = hostEl.querySelector(
			`.${LIMIT_MSG_CLASS}`
		);

		if (!noteEl) {
			noteEl = document.createElement("div");
			noteEl.className = LIMIT_MSG_CLASS;
			hostEl.appendChild(noteEl);
		}

		noteEl.textContent = msg;

		if (noteEl._hideTimer) {
			clearTimeout(noteEl._hideTimer);
		}

		noteEl._hideTimer = setTimeout(() => {
			if (noteEl.parentNode) {
				noteEl.parentNode.removeChild(noteEl);
			}
		}, 6000);
	};

	/* ---------------------------------------------------------------------------
	 * Add to cart
	 * ------------------------------------------------------------------------- */

	/**
	 * Add a product form to the cart, respecting per-variant limits,
	 * and open the drawer immediately so it never waits on the
	 * limit-check + add round-trips.
	 */
	const addToCartFromForm = async ({
		formData,
		variantId,
		handle,
		qtyRequested,
		hostEl,
	}) => {
		/*
		 * Cart has items → show them under a lines loader;
		 * empty cart → full loader.
		 */
		const hasItems = !!(
			lastCart &&
			lastCart.item_count > 0
		);

		openDrawer(
			hasItems ? lastCart : undefined,
			{
				loading: hasItems ? "lines" : "full",
			}
		);

		const { max, inCart } =
			(await getLimitContext(
				variantId,
				handle
			)) || {
				max: Infinity,
				inCart: 0,
			};

		const allowed = Math.max(
			0,
			max - inCart
		);

		if (
			max !== Infinity &&
			allowed <= 0
		) {
			const msg =
				max === 1
					? "Only 1 of this piece can be added per order — it's already in your bag."
					: `Maximum of ${max} reached — already in your bag.`;

			openDrawer();

			await renderInFlight;

			if (!showLineMessage(variantId, msg)) {
				showLimitMessage(hostEl, msg);
			}

			return {
				ok: false,
				reason: "blocked",
			};
		}

		let qtyToAdd = qtyRequested;
		let truncated = false;

		if (
			max !== Infinity &&
			qtyRequested > allowed
		) {
			qtyToAdd = allowed;
			truncated = true;
		}

		formData.set(
			"quantity",
			String(qtyToAdd)
		);

		let res;

		try {
			res = await fetch(
				ENDPOINTS.add,
				{
					method: "POST",
					headers: {
						Accept: "application/json",
					},
					body: formData,
				}
			);
		} catch (e) {
			window.location = `${SHOP_ROOT}cart`;

			return {
				ok: false,
				reason: "network",
			};
		}

		if (!res.ok) {
			const err = await res
				.json()
				.catch(() => ({}));

			showLimitMessage(
				hostEl,
				err.description ||
					err.message ||
					"Could not add to bag."
			);

			openDrawer();

			return {
				ok: false,
				reason: "rejected",
			};
		}

		const added = await res
			.json()
			.catch(() => null);

		const addedItem =
			added && added.items
				? added.items[
						added.items.length - 1
					]
				: added;

		if (addedItem?.product_id) {
			lastAddedProductId =
				addedItem.product_id;
		}

		const updatedCart =
			await getCart();

		await updateBubble(updatedCart);

		openDrawer(updatedCart);

		if (truncated) {
			await renderInFlight;

			const msg = `Only ${qtyToAdd} added — that's the maximum available.`;

			if (
				!showLineMessage(
					variantId,
					msg
				)
			) {
				showLimitMessage(hostEl, msg);
			}
		}

		return {
			ok: true,
			truncated,
		};
	};

	/* ---------------------------------------------------------------------------
	 * Markup builders
	 * ------------------------------------------------------------------------- */

	/** Build the HTML for every cart line. */
	const buildLinesHtml = (cart) =>
		cart.items
			.map((item, idx) => {
				const variantSuffix =
					item.variant_title &&
					item.variant_title !==
						"Default Title"
						? `<span class="${testId}_line-variant">${escapeHtml(
								item.variant_title
							)}</span>`
						: "";

				return `
					<div
						class="${testId}_line"
						data-line="${idx + 1}"
						data-variant-id="${item.id}"
						data-handle="${escapeHtml(item.handle)}"
					>
						<a
							class="${testId}_line-image"
							href="${escapeHtml(item.url)}"
						>
							<img
								src="${escapeHtml(item.image)}"
								alt="${escapeHtml(item.product_title)}"
								loading="lazy"
							>
						</a>

						<div class="${testId}_line-meta">
							<a
								class="${testId}_line-title"
								href="${escapeHtml(item.url)}"
							>
								${escapeHtml(item.product_title)}
							</a>

							${variantSuffix}

							<span class="${testId}_line-price">
								${
									String(item.properties?._requires_order_review) === "true"
										? "POA"
										: formatMoney(item.final_line_price)
								}
							</span>

							<div class="${testId}_line-actions">
								<div class="${testId}_qty">
									<button
										class="${testId}_qty-btn"
										data-act="dec"
										type="button"
										aria-label="Decrease quantity"
									>
										${MINUS_ICON_SVG}
									</button>

									<span class="${testId}_qty-value">
										${item.quantity}
									</span>

									<button
										class="${testId}_qty-btn"
										data-act="inc"
										type="button"
										aria-label="Increase quantity"
									>
										${PLUS_ICON_SVG}
									</button>
								</div>

								<button
									class="${testId}_line-remove"
									data-act="rm"
									type="button"
									aria-label="Remove from bag"
								>
									${REMOVE_ICON_SVG}
								</button>
							</div>
						</div>
					</div>
				`;
			})
			.join("");

	/**
	 * Build the "Keep Exploring" cross-sell block,
	 * or empty string when there are no recs.
	 */
	const buildCrossSellHtml = (products) => {
		if (!products.length) {
			return "";
		}

		const cards = products
			.map(
				(p) => `
					<div class="${testId}_cross-item">
						<a
							class="${testId}_cross-image"
							href="${escapeHtml(p.url)}"
						>
							<img
								src="${escapeHtml(p.featured_image)}"
								alt="${escapeHtml(p.title)}"
								loading="lazy"
							>
						</a>

						<div class="${testId}_cross-meta">
							<a
								class="${testId}_cross-title"
								href="${escapeHtml(p.url)}"
							>
								${escapeHtml(p.title)}
							</a>

							<span class="${testId}_cross-price">
								${formatMoney(p.price)}
							</span>
						</div>
					</div>
				`
			)
			.join("");

		return `
			<div class="${testId}_cross">
				<h3 class="${testId}_cross-heading">
					Keep Exploring
				</h3>

				<div class="${testId}_cross-grid">
					${cards}
				</div>
			</div>
		`;
	};

	/** Update drawer CTAs depending on whether the cart requires review. */
		const updateFooterCtas = (cart) => {
			const requiresReview =
				cartRequiresReview(cart);

			if (requiresReview) {
				checkoutEl.textContent =
					"Submit order for review";

				checkoutEl.href =
					`${SHOP_ROOT}cart#order-review`;

				viewCartEl.style.display =
					"none";
			} else {
				checkoutEl.textContent =
					"Checkout";

				checkoutEl.href =
					`${SHOP_ROOT}checkout`;

				viewCartEl.style.display =
					"";
			}
		};

	/* ---------------------------------------------------------------------------
	 * Rendering
	 * ------------------------------------------------------------------------- */

	/** Render the empty-cart state. */
	const renderEmpty = () => {
		drawerEl.classList.remove(
			`${testId}_drawer--loading`
		);

		drawerTitleEl.textContent =
			"Your cart (0)";

		drawerBodyEl.classList.add(
			`${testId}_drawer-body--empty`
		);

		drawerBodyEl.innerHTML = `
			<div class="${testId}_empty">
				<p class="${testId}_empty-message">
					Your cart is empty
				</p>

				<button
					class="${testId}_empty-cta"
					type="button"
				>
					Continue Shopping
				</button>
			</div>
		`;

		drawerFooterEl.hidden = true;
	};

	/**
	 * Render the full drawer
	 * (lines + cross-sell + footer)
	 * for the given or fetched cart.
	 */
	const renderDrawer = async (
		cartArg
	) => {
		if (!cartArg) {
			drawerBodyEl.innerHTML = `
				<p class="${testId}_loading">
					Loading…
				</p>
			`;
		}

		const cart =
			cartArg || (await getCart());

		if (
			!cart ||
			!cart.item_count
		) {
			renderEmpty();
			return;
		}

		drawerBodyEl.classList.remove(
			`${testId}_drawer-body--empty`
		);

		drawerFooterEl.hidden = false;

		drawerTitleEl.textContent =
			`Your cart (${cart.item_count})`;

		totalValueEl.innerHTML =
			cartRequiresReview(cart)
				? "POA"
				: formatMoney(cart.total_price);

		updateFooterCtas(cart);

		const inCart =
			cart.items.some(
				(item) =>
					item.product_id ===
					lastAddedProductId
			);

		const productId = inCart
			? lastAddedProductId
			: cart.items[
					cart.items.length - 1
				].product_id;

		const recs =
			await getRecommendations(
				productId
			);

		drawerEl.classList.remove(
			`${testId}_drawer--loading`
		);

		drawerBodyEl.innerHTML =
			buildLinesHtml(cart) +
			buildCrossSellHtml(recs);
	};

	/**
	 * Serialise renders so overlapping cart writes
	 * can't interleave their output.
	 */
	const renderDrawerSafe = (
		cartArg
	) => {
		renderInFlight = (
			renderInFlight ||
			Promise.resolve()
		).then(() =>
			renderDrawer(cartArg)
		);

		return renderInFlight;
	};

	/**
	 * Synchronously render the current cart's lines
	 * (no recommendations) — used to show the
	 * existing cart instantly under a loader
	 * while an add finalises.
	 */
	const renderCartImmediate = (
		cart
	) => {
		drawerBodyEl.classList.remove(
			`${testId}_drawer-body--empty`
		);

		drawerFooterEl.hidden = false;

		drawerTitleEl.textContent =
			`Your cart (${cart.item_count})`;

		totalValueEl.innerHTML =
			cartRequiresReview(cart)
				? "POA"
				: formatMoney(cart.total_price);

		updateFooterCtas(cart);

		drawerBodyEl.innerHTML =
			buildLinesHtml(cart);
	};

	/* ---------------------------------------------------------------------------
	 * Open / close
	 * ------------------------------------------------------------------------- */

	/**
	 * Open the drawer and render its contents
	 * for the given loading mode / cart.
	 */
	const openDrawer = (
		cartArg,
		opts
	) => {
		rootEl.classList.add(
			`${testId}_is-open`
		);

		drawerEl.setAttribute(
			"aria-hidden",
			"false"
		);

		overlayEl.setAttribute(
			"aria-hidden",
			"false"
		);

		document.body.classList.add(
			`${testId}_no-scroll`
		);

		const mode = opts?.loading;

		if (
			mode === "lines" &&
			lastCart &&
			lastCart.item_count > 0
		) {
			/*
			 * Cart has items — show them right away
			 * with a loader over the product lines.
			 */
			renderCartImmediate(lastCart);

			drawerEl.classList.add(
				`${testId}_drawer--loading`
			);
		} else if (mode) {
			/*
			 * Empty cart (or cart not yet known) —
			 * full loader, not the empty-cart message.
			 */
			drawerBodyEl.classList.remove(
				`${testId}_drawer-body--empty`
			);

			drawerFooterEl.hidden = true;

			drawerBodyEl.innerHTML = "";

			drawerEl.classList.add(
				`${testId}_drawer--loading`
			);
		} else if (cartArg) {
			/*
			 * We already have a fresh cart
			 * (e.g. just after an add) —
			 * render it directly.
			 */
			renderDrawerSafe(cartArg);
		} else if (lastCart) {
			/*
			 * Cart-icon open — show the cached cart
			 * instantly, then refresh silently
			 * (no loader).
			 */
			if (
				lastCart.item_count > 0
			) {
				renderCartImmediate(
					lastCart
				);
			} else {
				renderEmpty();
			}

			getCart().then((fresh) =>
				renderDrawerSafe(fresh)
			);
		} else {
			/*
			 * Cold open before the cache warmed —
			 * fall back to the fetch-and-loading path.
			 */
			renderDrawerSafe();
		}
	};

	/** Close the drawer. */
	const closeDrawer = () => {
		if (!isDrawerOpen()) {
			return;
		}

		rootEl.classList.remove(
			`${testId}_is-open`
		);

		drawerEl.setAttribute(
			"aria-hidden",
			"true"
		);

		overlayEl.setAttribute(
			"aria-hidden",
			"true"
		);

		document.body.classList.remove(
			`${testId}_no-scroll`
		);
	};

	/** Whether the drawer is currently open. */
	const isDrawerOpen = () =>
		rootEl &&
		rootEl.classList.contains(
			`${testId}_is-open`
		);

	/* ---------------------------------------------------------------------------
	 * Quantity changes
	 * ------------------------------------------------------------------------- */

	/**
	 * Change a line's quantity via the AJAX cart.
	 * One cart write at a time — ignore further
	 * qty clicks until the in-flight request resolves;
	 * the clicked line shows a spinner while it syncs.
	 */
	const changeQty = async (
		variantId,
		quantity,
		lineEl
	) => {
		if (cartBusy) {
			return;
		}

		cartBusy = true;

		if (lineEl) {
			lineEl.classList.add(
				`${testId}_line--loading`
			);
		}

		try {
			let clampedAt = null;

			if (
				lineEl &&
				quantity > 0
			) {
				const product =
					await getProductByHandle(
						lineEl.dataset.handle
					);

				const variant =
					product?.variants?.find(
						(v) =>
							String(v.id) ===
							String(variantId)
					);

				const max =
					effectiveMaxFor(variant);

				if (quantity > max) {
					clampedAt = max;
					quantity = max;
				}
			}

			const res = await fetch(
				ENDPOINTS.change,
				{
					method: "POST",
					headers: {
						"Content-Type":
							"application/json",
						Accept:
							"application/json",
					},
					body: JSON.stringify({
						id: variantId,
						quantity,
					}),
				}
			);

			const updatedCart =
				await res
					.json()
					.catch(() => null);

			await updateBubble(
				updatedCart
			);

			await renderDrawerSafe(
				updatedCart
			);

			if (
				clampedAt !== null
			) {
				const msg =
					clampedAt === 1
						? "Only 1 of this piece can be added per order — it's already in your bag."
						: `Maximum of ${clampedAt} reached — already in your bag.`;

				showLineMessage(
					variantId,
					msg
				);
			}
		} finally {
			cartBusy = false;
		}
	};

	/* ---------------------------------------------------------------------------
	 * DOM setup + interaction
	 * ------------------------------------------------------------------------- */

	/**
	 * Insert the drawer markup into the page
	 * and cache element references.
	 */
	const createDrawer = (bodyEl) => {
		if (
			bodyEl.querySelector(
				`.${testId}_root`
			)
		) {
			return;
		}

		rootEl =
			document.createElement("div");

		rootEl.className =
			`${testId}_root`;

		rootEl.innerHTML =
			DRAWER_HTML;

		bodyEl.appendChild(rootEl);

		overlayEl =
			rootEl.querySelector(
				`.${testId}_overlay`
			);

		drawerEl =
			rootEl.querySelector(
				`.${testId}_drawer`
			);

		drawerTitleEl =
			drawerEl.querySelector(
				`.${testId}_drawer-title`
			);

		drawerBodyEl =
			drawerEl.querySelector(
				`.${testId}_drawer-body`
			);

		drawerFooterEl =
			drawerEl.querySelector(
				`.${testId}_drawer-footer`
			);

		totalValueEl =
			drawerFooterEl.querySelector(
				`.${testId}_total-value`
			);

		checkoutEl =
			drawerFooterEl.querySelector(
				`.${testId}_checkout`
			);

		viewCartEl =
			drawerFooterEl.querySelector(
				`.${testId}_viewcart`
			);
	};
 
	/**
	 * Whether a form is an add-to-cart form
	 * (and not a request-a-quote / installment form).
	 */
	const isAtcForm = (formEl) =>
		formEl instanceof
			HTMLFormElement &&
		/\/cart\/add/.test(
			formEl.action || ""
		) &&
		!formEl.classList.contains(
			"rfq-product-form"
		) &&
		!formEl.classList.contains(
			"installment"
		);

	/**
	 * Read a product form and add it
	 * to the cart via the drawer.
	 */
	const submitFormToCart = (
		formEl,
		hostEl
	) => {
		const fd =
			new FormData(formEl);

		const variantId =
			fd.get("id");

		const qtyRequested =
			parseInt(
				fd.get("quantity") || "1",
				10
			);

		const handle =
			getHandleFromPage();

		return addToCartFromForm({
			formData: fd,
			variantId,
			handle,
			qtyRequested,
			hostEl:
				hostEl || formEl,
		});
	};

	/**
	 * Wire up all drawer interactions:
	 * ATC submit, drawer clicks,
	 * and Escape-to-close.
	 */
	const setupInterceptors = () => {
		/*
		 * Intercept ATC form submissions
		 * so the add runs through the drawer.
		 */
		document.addEventListener(
			"submit",
			(e) => {
				const formEl =
					e.target;

				if (
					!isAtcForm(formEl)
				) {
					return;
				}

				e.preventDefault();

				e.stopImmediatePropagation();

				submitFormToCart(
					formEl,
					formEl.closest(
						"product-form"
					) || formEl
				);
			},
			true
		);

		document.addEventListener(
			"click",
			(e) => {
				const qtyOrRemoveBtn =
					e.target.closest(
						`.${testId}_qty-btn, .${testId}_line-remove`
					);

				if (qtyOrRemoveBtn) {
					e.preventDefault();

					const lineEl =
						qtyOrRemoveBtn.closest(
							`.${testId}_line`
						);

					const variantId =
						lineEl.dataset
							.variantId;

					const currentQty =
						parseInt(
							lineEl.querySelector(
								`.${testId}_qty-value`
							).textContent,
							10
						);

					const act =
						qtyOrRemoveBtn
							.dataset.act;

					const target =
						act === "inc"
							? currentQty + 1
							: act === "dec"
								? Math.max(
										0,
										currentQty - 1
									)
								: 0;

					changeQty(
						variantId,
						target,
						lineEl
					);

					return;
				}

				const closeBtn =
					e.target.closest(
						`.${testId}_drawer-close, .${testId}_empty-cta`
					);

				if (closeBtn) {
					closeDrawer();
					return;
				}

				if (
					e.target ===
					overlayEl
				) {
					closeDrawer();
					return;
				}

				const cartLinkEl =
					e.target.closest(
						SELECTORS.cartLink
					);

				if (
					cartLinkEl &&
					!cartLinkEl.closest(
						`.${testId}_drawer`
					)
				) {
					e.preventDefault();

					openDrawer();

					return;
				}
			},
			true
		);

		document.addEventListener(
			"keydown",
			(e) => {
				if (
					e.key ===
						"Escape" &&
					isDrawerOpen()
				) {
					closeDrawer();
				}
			}
		);
	};

	/**
	 * Build the drawer, wire interactions,
	 * and warm the cart cache.
	 */
	const init = (bodyEl) => {
		createDrawer(bodyEl);

		setupInterceptors();

        // Public bridge for collection quick add: one request through existing limits and drawer.
        window.ArdmoreCartDrawer = {
            add: ({variantId, handle, quantity = 1, hostEl}) => {
                const formData = new FormData();
                formData.set("id", String(variantId));
                formData.set("quantity", String(quantity));
                return addToCartFromForm({formData, variantId, handle, qtyRequested:quantity, hostEl});
            }
        };


		/*
		 * Warm the cart cache so the first add
		 * can show existing lines under a loader
		 * (no empty flash).
		 */
		getCart();
	};

	if (document.body) {
		init(document.body);
	} else {
		document.addEventListener(
			"DOMContentLoaded",
			() => init(document.body)
		);
	}
})();